// src/pages/achats/AjouterFournisseur.jsx
import React, { useState } from "react";
import {
    Card, Row, Col, Form, Button, Spinner, InputGroup
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function AjouterFournisseur() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [formData, setFormData] = useState({
        code_fournisseur: '',
        raison_sociale: '',
        adresse: '',
        telephone: '',
        email: '',
        ice: '',
        patente: '',
        rc: '',
        identifiant_fiscal: '',
        cnss: '',
        conditions_reglement: ''
    });
    const [errors, setErrors] = useState({});

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    /* ---------- Handlers ---------- */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.raison_sociale) newErrors.raison_sociale = "La raison sociale est requise";
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Format d'email invalide";
        }
        return newErrors;
    };

    const resetForm = () => {
        setFormData({
            code_fournisseur: '', raison_sociale: '', adresse: '', telephone: '',
            email: '', ice: '', patente: '', rc: '', identifiant_fiscal: '',
            cnss: '', conditions_reglement: ''
        });
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            showToast('error', 'Champs invalides', 'Veuillez vérifier les champs du formulaire');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/fournisseurs.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'create', ...formData })
            });
            const result = await response.json();

            if (result.success) {
                showToast('success', 'Fournisseur créé', 'Enregistrement réussi ! Redirection...');
                setTimeout(() => navigate('/achats/fournisseurs'), 1500);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la création");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setLoading(false);
        }
    };

    /* ---------- Rendu ---------- */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    <ToastNotification
                        show={toast.show}
                        onClose={hideToast}
                        type={toast.type}
                        title={toast.title}
                        message={toast.message}
                    />

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-user-add-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Nouveau fournisseur</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-add-circle-line"></i>
                                        Ajouter un nouveau fournisseur
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate(-1)}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/achats/fournisseurs')}>
                                <i className="ri-list-check"></i> Liste
                            </Button>
                        </div>
                    </div>

                    {/* ===== FORMULAIRE ===== */}
                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-user-add-line me-2"></i>
                                        Formulaire d'ajout
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">

                                            {/* SECTION INFORMATIONS GÉNÉRALES */}
                                            <Col md={12}>
                                                <div className="section-title-charte">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                        <i className="ri-information-line"></i>
                                                    </span>
                                                    <h6>Informations générales</h6>
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Raison sociale <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-building-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="raison_sociale"
                                                            value={formData.raison_sociale}
                                                            onChange={handleChange}
                                                            isInvalid={!!errors.raison_sociale}
                                                            placeholder="Nom de l'entreprise"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.raison_sociale}
                                                        </Form.Control.Feedback>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Code fournisseur</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-barcode-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="code_fournisseur"
                                                            value={formData.code_fournisseur}
                                                            onChange={handleChange}
                                                            placeholder="Auto si vide"
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        Généré automatiquement si non renseigné
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION COORDONNÉES */}
                                            <Col md={12}>
                                                <div className="section-title-charte mt-2">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                        <i className="ri-phone-line"></i>
                                                    </span>
                                                    <h6>Coordonnées</h6>
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Téléphone</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-phone-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="telephone"
                                                            value={formData.telephone}
                                                            onChange={handleChange}
                                                            placeholder="+225 XX XX XX XX XX"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Email</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={handleChange}
                                                            isInvalid={!!errors.email}
                                                            placeholder="contact@exemple.com"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.email}
                                                        </Form.Control.Feedback>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Adresse</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-map-pin-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            name="adresse"
                                                            value={formData.adresse}
                                                            onChange={handleChange}
                                                            placeholder="Adresse complète"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION IDENTIFIANTS JURIDIQUES */}
                                            <Col md={12}>
                                                <div className="section-title-charte mt-2">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                                        <i className="ri-briefcase-line"></i>
                                                    </span>
                                                    <h6>Identifiants juridiques</h6>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">ICE</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-hashtag"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="ice"
                                                            value={formData.ice}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Patente</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-file-text-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="patente"
                                                            value={formData.patente}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">RC</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-file-list-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="rc"
                                                            value={formData.rc}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Identifiant Fiscal (IF)</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-government-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="identifiant_fiscal"
                                                            value={formData.identifiant_fiscal}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">CNSS</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-shield-check-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="cnss"
                                                            value={formData.cnss}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION CONDITIONS */}
                                            <Col md={12}>
                                                <div className="section-title-charte mt-2">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                                        <i className="ri-file-text-line"></i>
                                                    </span>
                                                    <h6>Conditions de règlement</h6>
                                                </div>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-file-text-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            name="conditions_reglement"
                                                            value={formData.conditions_reglement}
                                                            onChange={handleChange}
                                                            placeholder="Ex: Paiement à 30 jours, escompte de 2%..."
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* BOUTONS D'ACTION */}
                                            <Col md={12}>
                                                <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                                                <div className="d-flex justify-content-end gap-3 flex-wrap">
                                                    <Button onClick={resetForm} className="btn-modern-outline" type="button">
                                                        <i className="ri-refresh-line me-1"></i> Réinitialiser
                                                    </Button>
                                                    <Button onClick={() => navigate('/achats/fournisseurs')} className="btn-modern-outline" type="button">
                                                        Annuler
                                                    </Button>
                                                    <Button type="submit" disabled={loading} className="btn-modern-primary">
                                                        {loading ? (
                                                            <><Spinner size="sm" className="me-2" />Enregistrement...</>
                                                        ) : (
                                                            <><i className="ri-save-line me-2"></i>Enregistrer</>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE (identiques à Decaissement/Contrats) ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}